import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { toast } from "sonner"
import {
  Link,
  FileText,
  Loader2,
  Check,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"

const isPDF = (url: string) => /\.pdf$/i.test(url)

function formatDateTime(timestamp?: string) {
  if (!timestamp) return ""
  const d = new Date(timestamp)
  const dd = String(d.getDate()).padStart(2, "0")
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const yyyy = d.getFullYear()
  const time = d.toLocaleTimeString(getIntlLocale(), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
  return `${dd}.${mm}.${yyyy} ${time}`
}

function DeliveryOrderPreview({ order }: { order: DeliveryOrderOption }) {
  const { t } = useTranslation()
  const mappedUrl = mapFileUrlToSameOrigin(order.fileUrl)
  const pdf = isPDF(order.fileUrl)
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const dragging = useRef(false)
  const lastPos = useRef({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  // Reset zoom/pan when order changes
  useEffect(() => {
    setScale(1)
    setTranslate({ x: 0, y: 0 })
  }, [order.messageId])

  const zoomIn = useCallback(() => setScale((s) => Math.min(s * 1.3, 8)), [])
  const zoomOut = useCallback(() => {
    setScale((s) => {
      const next = Math.max(s / 1.3, 1)
      if (next === 1) setTranslate({ x: 0, y: 0 })
      return next
    })
  }, [])
  const resetZoom = useCallback(() => {
    setScale(1)
    setTranslate({ x: 0, y: 0 })
  }, [])

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    if (e.deltaY < 0) {
      setScale((s) => Math.min(s * 1.15, 8))
    } else {
      setScale((s) => {
        const next = Math.max(s / 1.15, 1)
        if (next === 1) setTranslate({ x: 0, y: 0 })
        return next
      })
    }
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return
    dragging.current = true
    lastPos.current = { x: e.clientX, y: e.clientY }
  }, [])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current) return
    const dx = e.clientX - lastPos.current.x
    const dy = e.clientY - lastPos.current.y
    lastPos.current = { x: e.clientX, y: e.clientY }
    setTranslate((prev) => ({ x: prev.x + dx, y: prev.y + dy }))
  }, [])

  const handleMouseUp = useCallback(() => {
    dragging.current = false
  }, [])

  return (
    <div className="flex h-full flex-col">
      <div className="mb-1 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">
            {order.fileName.replace(/\.[^.]+$/, "")}
          </div>
          <div className="text-xs text-muted-foreground">
            {new Date(order.timestamp).toLocaleString(getIntlLocale())}
          </div>
        </div>
        {!pdf && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={zoomOut}
              title={t("reports.linkDeliveryOrder.zoomOut")}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="min-w-[3ch] text-center text-xs text-muted-foreground">
              {Math.round(scale * 100)}%
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={zoomIn}
              title={t("reports.linkDeliveryOrder.zoomIn")}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            {scale !== 1 && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={resetZoom}
                title={t("reports.linkDeliveryOrder.resetZoom")}
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
      <div
        ref={containerRef}
        className="min-h-0 flex-1 overflow-hidden rounded-md border bg-muted"
        style={{ cursor: !pdf && scale > 1 ? "grab" : undefined }}
        onWheel={!pdf ? handleWheel : undefined}
        onMouseDown={!pdf ? handleMouseDown : undefined}
        onMouseMove={!pdf ? handleMouseMove : undefined}
        onMouseUp={!pdf ? handleMouseUp : undefined}
        onMouseLeave={!pdf ? handleMouseUp : undefined}
      >
        {pdf ? (
          <iframe
            src={mappedUrl}
            className="h-full w-full"
            title={order.fileName}
          />
        ) : (
          <img
            src={mappedUrl}
            alt={order.fileName}
            className="h-full w-full select-none object-contain"
            draggable={false}
            style={{
              transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
              transformOrigin: "center center",
              transition: dragging.current ? "none" : "transform 0.15s ease",
            }}
          />
        )}
      </div>
    </div>
  )
}

export type DeliveryOrderOption = {
  messageId: string
  fileUrl: string
  fileName: string
  timestamp: string
  chatJid: string
}

export type LinkDocumentInfo = {
  cmrMessageId: string
  cmrFileUrl: string
  cmrFileName: string
  chatJid: string
}

export function LinkDeliveryOrderDialog({
  open,
  onOpenChange,
  driverJid,
  cmrInfo,
  onLinked,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  driverJid: string
  cmrInfo: LinkDocumentInfo
  onLinked?: () => void
}) {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const [orders, setOrders] = useState<DeliveryOrderOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLinking, setIsLinking] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!open || !client || !driverJid) return
    setIsLoading(true)
    setError(false)
    setSelected(null)
    setOrders([])

    AdHocCommands.getDriverDeliveryOrders(client, driverJid, 5)
      .then((result) => {
        const jsonElement = result
          .getChild("command", "http://jabber.org/protocol/commands")
          ?.getChild("json", "urn:xmpp:json:0")
        if (!jsonElement) throw new Error("No JSON response")
        const parsed = JSON.parse(jsonElement.text())
        setOrders(parsed.orders || [])
      })
      .catch((err) => {
        console.error("Failed to fetch delivery orders:", err)
        setError(true)
      })
      .finally(() => setIsLoading(false))
  }, [open, client, driverJid])

  const handleLink = useCallback(async () => {
    if (!client || !selected) return
    const order = orders.find((o) => o.messageId === selected)
    if (!order) return

    setIsLinking(true)
    try {
      await AdHocCommands.linkDocument({
        client,
        cmrMessageId: cmrInfo.cmrMessageId,
        deliveryOrderMessageId: order.messageId,
        cmrFileUrl: cmrInfo.cmrFileUrl,
        cmrFileName: cmrInfo.cmrFileName,
        deliveryOrderFileUrl: order.fileUrl,
        deliveryOrderFileName: order.fileName,
        messageRoomJid: cmrInfo.chatJid,
        triggeringRoomJid: order.chatJid || cmrInfo.chatJid,
      })
      toast.success(t("reports.linkDeliveryOrder.linked"), {
        classNames: { toast: "!justify-center" },
      })
      onOpenChange(false)
      onLinked?.()
    } catch (err) {
      console.error("Linking failed:", err)
      toast.error(t("reports.linkDeliveryOrder.linkFailed"), {
        classNames: { toast: "!justify-center" },
      })
    } finally {
      setIsLinking(false)
    }
  }, [client, selected, orders, cmrInfo, onOpenChange, onLinked, t])

  const selectedOrder = useMemo(
    () => orders.find((o) => o.messageId === selected),
    [orders, selected],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[10010] max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t("reports.linkDeliveryOrder.title")}</DialogTitle>
          <DialogDescription>
            {t("reports.linkDeliveryOrder.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4" style={{ height: "60vh" }}>
          {/* Left: order list */}
          <div className="w-64 flex-shrink-0 space-y-2 overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {error && (
              <div className="py-4 text-center text-sm text-red-500">
                {t("reports.linkDeliveryOrder.fetchFailed")}
              </div>
            )}

            {!isLoading && !error && orders.length === 0 && (
              <div className="py-4 text-center text-sm text-muted-foreground">
                {t("reports.linkDeliveryOrder.empty")}
              </div>
            )}

            {orders.map((order) => {
              const isSelected = selected === order.messageId
              const mappedUrl = mapFileUrlToSameOrigin(order.fileUrl)
              const pdf = isPDF(order.fileUrl)

              return (
                <button
                  key={order.messageId}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                      : "border-border hover:bg-muted"
                  }`}
                  onClick={() => setSelected(order.messageId)}
                >
                  <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-md border bg-muted">
                    {pdf ? (
                      <div className="flex h-full w-full items-center justify-center bg-red-50 dark:bg-red-950">
                        <FileText className="h-6 w-6 text-red-500" />
                      </div>
                    ) : (
                      <img
                        src={mappedUrl}
                        alt={order.fileName}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="truncate text-sm font-medium">
                      {order.fileName.replace(/\.[^.]+$/, "")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(order.timestamp)}
                    </p>
                  </div>
                  {isSelected && (
                    <Check className="h-5 w-5 flex-shrink-0 text-blue-500" />
                  )}
                </button>
              )
            })}
          </div>

          {/* Right: preview */}
          <div className="min-w-0 flex-1">
            {selectedOrder ? (
              <DeliveryOrderPreview order={selectedOrder} />
            ) : (
              <div className="flex h-full items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                {t("reports.linkDeliveryOrder.selectToPreview")}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleLink} disabled={!selected || isLinking}>
            {isLinking ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Link className="mr-1 h-4 w-4" />
            )}
            {t("reports.linkDeliveryOrder.link")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
